import React, { useState, useEffect } from 'react'
import { 
  ArrowLeft,
  Plus,
  Edit,
  User,
  MapPin,
  Phone
} from 'lucide-react'
import { Breadcrumb } from '../../components/Common'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'

const CustomerMasterView = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const customerData = location.state?.customerData || {}

  const [activeTab, setActiveTab] = useState('customerInfo')
  const [formData, setFormData] = useState({
    // Customer Info - Matching backend model
    customerCode: '',
    customerName: '', // Backend: customerName
    customerNickName: '',
    customerCategory: '',
    customerType: '',
    region: '', // Required field
    customerUdyogAadhar: '',
    customerPAN: '', // Backend: customerPAN
    GSTIN: '', // Backend: GSTIN
    GSTClassification: '', // Backend: GSTClassification
    creditLimit: '',
    customerPaymentTerms: '', // Backend: customerPaymentTerms
    customerCurrency: 'INR', // Backend: customerCurrency
    frightCharge: '',
    transporter: '',
    destination: '',
    customerWebsite: '',
    eximCode: '',
    transporterGSTIN: '',
    transporterId: '',
    categoryType: 'Domestic',
    isCustomerActive: 'A', // Backend: isCustomerActive
    // Legacy fields for form compatibility
    customerNameOnTaxInvoice: '',
    panCardNo: '',
    paymentTerms: '',
    gstin: '',
    gstClassification: '',
    currency: '',
    status: 'Active'
  })

  const [addresses, setAddresses] = useState([
    {
      id: 1,
      country: '',
      stateProvince: '',
      cityDistrict: '',
      pinCode: '',
      addressLine1: '',
      addressLine2: '',
      addressLine3: '',
      addressLine4: ''
    }
  ])

  const [contacts, setContacts] = useState([
    {
      id: 1,
      contactPerson: '',
      email: '',
      mobile: '',
      alternatePhone: '',
      department: '',
      designation: ''
    }
  ])

  // Pre-fill form data when component mounts
  useEffect(() => {
    setFormData({
      // Map backend fields to form fields
      customerCode: customerData.customerCode || '',
      customerName: customerData.customerName || '',
      customerNickName: customerData.customerNickName || '',
      customerCategory: customerData.customerCategory || '',
      customerType: customerData.customerType || '',
      region: customerData.region || '',
      customerUdyogAadhar: customerData.customerUdyogAadhar || '',
      customerPAN: customerData.customerPAN || '',
      GSTIN: customerData.GSTIN || '',
      GSTClassification: customerData.GSTClassification || '',
      creditLimit: customerData.creditLimit || '',
      customerPaymentTerms: customerData.customerPaymentTerms || '',
      customerCurrency: customerData.customerCurrency || 'INR',
      frightCharge: customerData.frightCharge || '',
      transporter: customerData.transporter || '',
      destination: customerData.destination || '',
      customerWebsite: customerData.customerWebsite || '',
      eximCode: customerData.eximCode || '',
      transporterGSTIN: customerData.transporterGSTIN || '',
      transporterId: customerData.transporterId || '',
      categoryType: customerData.categoryType || 'Domestic',
      isCustomerActive: customerData.isCustomerActive || 'A',
      // Legacy mapping for backward compatibility
      customerNameOnTaxInvoice: customerData.customerName || '',
      panCardNo: customerData.customerPAN || '',
      paymentTerms: customerData.customerPaymentTerms || '',
      gstin: customerData.GSTIN || '',
      gstClassification: customerData.GSTClassification || '',
      currency: customerData.customerCurrency || 'INR',
      status: customerData.isCustomerActive === 'A' ? 'Active' : 'Inactive'
    })
    
    // Map addresses from backend data
    if (customerData.customerBillingAddress && customerData.customerBillingAddress.length > 0) {
      setAddresses(customerData.customerBillingAddress.map((addr, index) => ({
        id: index + 1,
        country: addr.country || '',
        stateProvince: addr.state || '',
        cityDistrict: addr.city || '',
        pinCode: addr.pinCode || '',
        addressLine1: addr.line1 || '',
        addressLine2: addr.line2 || '',
        addressLine3: addr.line3 || '',
        addressLine4: addr.line4 || ''
      })))
    }

    // Map contacts from backend data
    if (customerData.customerContactInfo && customerData.customerContactInfo.length > 0) {
      setContacts(customerData.customerContactInfo.map((contact, index) => ({
        id: index + 1,
        contactPerson: contact.contactPersonName || '',
        email: contact.contactPersonEmail || '',
        mobile: contact.contactPersonNumber || '',
        alternatePhone: '', // Not in backend model
        department: contact.contactPersonDepartment || '',
        designation: contact.contactPersonDesignation || ''
      })))
    }
  }, [customerData])

  const handleBack = () => {
    navigate('/sales/customer-master')
  }

  const handleEdit = () => {
    const customerId = customerData._id || customerData.id
    if (!customerId) {
      toast.error('Cannot edit: Customer ID is missing')
      return
    }
    navigate(`/sales/customer-master/edit/${customerId}`, { state: { customerData } })
  }

  // Breadcrumb items
  const breadcrumbItems = [
    {
      label: 'Sales',
      path: '/sales',
      onClick: () => navigate('/sales')
    },
    {
      label: 'Masters',
      path: '/sales',
      onClick: () => navigate('/sales')
    },
    {
      label: 'Customer Master',
      path: '/sales/customer-master',
      isCurrent: true
    }
  ]

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Customer Master - View</h1>
          <p>View customer information</p>
        </div>
        <div className="page-actions">
          <button className="btn-primary" onClick={handleEdit}>
            <Edit size={18} />
            Edit
          </button>
        </div>
      </div>

      {/* Form Section */}
      <div className="form-container">
        <div className="sac-form-header">
          <h2>Customer Master Entry</h2>
        </div>
        
        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button
            className={`tab-button ${activeTab === 'customerInfo' ? 'active' : ''}`}
            onClick={() => setActiveTab('customerInfo')}
          >
            <User size={18} />
            <span>Customer Info</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'customerAddress' ? 'active' : ''}`}
            onClick={() => setActiveTab('customerAddress')}
          >
            <MapPin size={18} />
            <span>Customer Address</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'contactDetails' ? 'active' : ''}`}
            onClick={() => setActiveTab('contactDetails')}
          >
            <Phone size={18} />
            <span>Contact Details</span>
          </button>
        </div>
          
        <form className="form-content">
          {/* Customer Info Tab */}
          {activeTab === 'customerInfo' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Customer Information</h3>
              </div>
              
              {/* Row 1: Customer Category, Customer Code, Customer Name, Customer Vendor Code */}
              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="customerCategory" className="form-label required">
                    Customer Category
                  </label>
                  <input
                    type="text"
                    id="customerCategory"
                    name="customerCategory"
                    value={formData.customerCategory}
                    readOnly
                    className="form-input readonly-input"
                    placeholder="Select Type"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="customerCode" className="form-label required">
                    Customer Code
                  </label>
                  <input
                    type="text"
                    id="customerCode"
                    name="customerCode"
                    value={formData.customerCode}
                    readOnly
                    className="form-input readonly-input"
                    placeholder="Enter customer code"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="customerName" className="form-label required">
                    Customer Name
                  </label>
                  <input
                    type="text"
                    id="customerName"
                    name="customerName"
                    value={formData.customerName}
                    readOnly
                    className="form-input readonly-input"
                    placeholder="Enter customer name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="region" className="form-label required">
                    Region
                  </label>
                  <input
                    type="text"
                    id="region"
                    name="region"
                    value={formData.region}
                    readOnly
                    className="form-input readonly-input"
                    placeholder="Select Region"
                  />
                </div>
              </div>

              {/* Row 2: GST Classification, GSTIN, PAN Card No, Currency */}
              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="GSTClassification" className="form-label required">
                    GST Classification
                  </label>
                  <input
                    type="text"
                    id="GSTClassification"
                    name="GSTClassification"
                    value={formData.GSTClassification}
                    readOnly
                    className="form-input readonly-input"
                    placeholder="GST Classification"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="GSTIN" className="form-label required">
                    GSTIN
                  </label>
                  <input
                    type="text"
                    id="GSTIN"
                    name="GSTIN"
                    value={formData.GSTIN}
                    readOnly
                    className="form-input readonly-input"
                    placeholder="Enter GSTIN"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="customerPAN" className="form-label required">
                    PAN Card No.
                  </label>
                  <input
                    type="text"
                    id="customerPAN"
                    name="customerPAN"
                    value={formData.customerPAN}
                    readOnly
                    className="form-input readonly-input"
                    placeholder="Enter PAN number"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="customerCurrency" className="form-label required">
                    Currency
                  </label>
                  <input
                    type="text"
                    id="customerCurrency"
                    name="customerCurrency"
                    value={formData.customerCurrency}
                    readOnly
                    className="form-input readonly-input"
                    placeholder="Select"
                  />
                </div>
              </div>

              {/* Row 3: Credit Limit, Payment Terms, Freight Terms, Status */}
              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="creditLimit" className="form-label">
                    Credit Limit
                  </label>
                  <input
                    type="text"
                    id="creditLimit"
                    name="creditLimit"
                    value={formData.creditLimit}
                    readOnly
                    className="form-input readonly-input"
                    placeholder="0.00"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="customerPaymentTerms" className="form-label required">
                    Payment Terms
                  </label>
                  <input
                    type="text"
                    id="customerPaymentTerms"
                    name="customerPaymentTerms"
                    value={formData.customerPaymentTerms}
                    readOnly
                    className="form-input readonly-input"
                    placeholder="Select"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="freightTerms" className="form-label">
                    Freight Terms
                  </label>
                  <input
                    type="text"
                    id="freightTerms"
                    name="freightTerms"
                    value={formData.freightTerms}
                    readOnly
                    className="form-input readonly-input"
                    placeholder="Select Freight Terms"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="status" className="form-label required">
                    Status
                  </label>
                  <input
                    type="text"
                    id="status"
                    name="status"
                    value={formData.status}
                    readOnly
                    className="form-input readonly-input"
                    placeholder="Status"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Customer Address Tab */}
          {activeTab === 'customerAddress' && (
            <div className="tab-content">
              <div className="form-section-header">
                <h3>Customer Addresses</h3>
              </div>
              
              {addresses.map((address, index) => (
                <div key={address.id} className="address-section">
                  <div className="address-header">
                    <h3>
                      {index === 0 ? 'Primary Address' : `Address ${index + 1}`}
                    </h3>
                  </div>
                  
                  {/* Row 1: Country, State/Province, City/District, Pin Code */}
                  <div className="grid-row-4">
                    <div className="form-group">
                      <label className="form-label required">Country</label>
                      <input
                        type="text"
                        value={address.country}
                        readOnly
                        className="form-input readonly-input"
                        placeholder="Select Country"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label required">State/Province</label>
                      <input
                        type="text"
                        value={address.stateProvince}
                        readOnly
                        className="form-input readonly-input"
                        placeholder="Select State"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label required">City/District</label>
                      <input
                        type="text"
                        value={address.cityDistrict}
                        readOnly
                        className="form-input readonly-input"
                        placeholder="Enter city"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label required">Pin Code</label>
                      <input
                        type="text"
                        value={address.pinCode}
                        readOnly
                        className="form-input readonly-input"
                        placeholder="Enter pin code"
                      />
                    </div>
                  </div>

                  {/* Row 2: Address Lines */}
                  <div className="grid-row-4">
                    <div className="form-group">
                      <label className="form-label required">Address Line 1</label>
                      <input
                        type="text"
                        value={address.addressLine1}
                        readOnly
                        className="form-input readonly-input"
                        placeholder="Enter address line 1"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label required">Address Line 2</label>
                      <input
                        type="text"
                        value={address.addressLine2}
                        readOnly
                        className="form-input readonly-input"
                        placeholder="Enter address line 2"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label required">Address Line 3</label>
                      <input
                        type="text"
                        value={address.addressLine3}
                        readOnly
                        className="form-input readonly-input"
                        placeholder="Enter address line 3"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Address Line 4</label>
                      <input
                        type="text"
                        value={address.addressLine4}
                        readOnly
                        className="form-input readonly-input"
                        placeholder="Enter address line 4"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Contact Details Tab */}
          {activeTab === 'contactDetails' && (
            <div className="tab-content">
              <div className="form-section-header">
                <h3>Contact Information</h3>
              </div>
              
              {contacts.map((contact, index) => (
                <div key={contact.id} className="contact-section">
                  <div className="contact-header">
                    <h3>
                      {index === 0 ? 'Primary Contact' : `Contact ${index + 1}`}
                    </h3>
                  </div>
                  
                  {/* Row 1: Contact Person, Mobile, Alternate Phone, Email */}
                  <div className="grid-row-4">
                    <div className="form-group">
                      <label className="form-label required">Contact Person</label>
                      <input
                        type="text"
                        value={contact.contactPerson}
                        readOnly
                        className="form-input readonly-input"
                        placeholder="Enter contact person name"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label required">Mobile</label>
                      <input
                        type="text"
                        value={contact.mobile}
                        readOnly
                        className="form-input readonly-input"
                        placeholder="Enter mobile number"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Alternate Phone</label>
                      <input
                        type="text"
                        value={contact.alternatePhone}
                        readOnly
                        className="form-input readonly-input"
                        placeholder="Enter alternate phone"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label required">Email</label>
                      <input
                        type="text"
                        value={contact.email}
                        readOnly
                        className="form-input readonly-input"
                        placeholder="Enter email address"
                      />
                    </div>
                  </div>

                  {/* Row 2: Department, Designation */}
                  <div className="grid-row-4">
                    <div className="form-group">
                      <label className="form-label">Department</label>
                      <input
                        type="text"
                        value={contact.department}
                        readOnly
                        className="form-input readonly-input"
                        placeholder="Enter department"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Designation</label>
                      <input
                        type="text"
                        value={contact.designation}
                        readOnly
                        className="form-input readonly-input"
                        placeholder="Enter designation"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </form>
      </div>
    </div>
  )
}

export default CustomerMasterView